import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  PropsWithChildren,
  useMemo,
} from 'react';
import { StyleSheet } from 'react-native';
import { Controller } from 'react-hook-form';
import { HubSectionType, HubType } from '@alucio/aws-beacon-amplify/src/models';
import {
  DNABox,
  DNAText,
  DNAButton,
  DNACard,
  DNAContextMenu,
  Iffy,
  TextField,
  } from '@alucio/lux-ui/src';
  import { Input } from '@ui-kitten/components';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { CommonIcon } from 'src/components/DNA';
import { ModifiedHubSection } from 'src/state/context/Hubs/HubsStateProvider';
import { EditHubRHForm, getWidgetFormName } from 'src/screens/Hubs/EditHub/useHubForm';
import { useHubsState } from 'src/state/context/Hubs/HubsStateProvider.proxy';
import { useHubRHFromState } from 'src/screens/Hubs/EditHub/useHubRHFormStateProvider';

// Widgets
import Textarea from 'src/screens/Hubs/EditHub/Widgets/Textarea';
import SharedFiles from 'src/screens/Hubs/EditHub/Widgets/SharedFiles';
import ToDoList from 'src/screens/Hubs/EditHub/Widgets/ToDoList';
import Links from 'src/screens/Hubs/EditHub/Widgets/Links';
import DNAPopover from 'src/components/DNA/Popover/DNAPopover';

export const widgetContentCardStyles = StyleSheet.create({
  card: {
    borderColor: colors['color-gray-80'],
    borderWidth: 1,
    borderRadius: 4,
  },
  cardHeader: {
    minHeight: 64,
    maxHeight: 64,
    paddingHorizontal: 24,
    borderBottomColor: colors['color-gray-80'],
    borderBottomWidth: 1,
  },
  cardBody: {
    minHeight: 72,
    padding: 24,
  },
  contextMenuItems: {
    minWidth: 160,
    right: 66,
  },
  editHubTitleIcon: {
    marginLeft: 6,
  },
  editHeaderInput: {
    maxWidth: '100%',
  },
  lockIconContainer: {
    marginRight: 8,
  },
});

export interface WidgetContentCardProps {
  widget: ModifiedHubSection,
  rhForm: EditHubRHForm,
}

const WidgetContentCardVariants: Record<HubSectionType, React.FC<WidgetContentCardProps>> = {
  [HubSectionType.TEXTAREA]: Textarea,
  [HubSectionType.SHARED_FILES]: SharedFiles,
  [HubSectionType.TODO_LIST]: ToDoList,
  [HubSectionType.LINKS]: Links,
}

const WidgetContentCard: React.FC<WidgetContentCardProps> = ({ widget, rhForm }) => {
  const WidgetVariant = WidgetContentCardVariants[widget.type]

  return (
    <DNACard
      style={ widgetContentCardStyles.card }
      appearance="flat"
    >
      <Controller
        name={getWidgetFormName(widget.id)}
        control={rhForm.control}
        render={() => (
          <WidgetVariant widget={widget} rhForm={rhForm}/>
        )}
      />
    </DNACard>
  )
}

export default WidgetContentCard


// Below are reusable components for widgets

type WidgetContextMenuProp = {
  widgetType: HubSectionType
  id: string
}

export const WidgetContextMenu: React.FC<WidgetContextMenuProp> = ({ widgetType, id }) => {
  const { handleDeleteWidget } = useHubsState()
  const { rhForm } = useHubRHFromState()
  return (
    <DNAContextMenu style={ widgetContentCardStyles.contextMenuItems }>
      <DNAContextMenu.Anchor>
        <DNAButton
          testID={`widget-section-header-${widgetType.toLowerCase()}-ellipsis-button`}
          appearance="outline"
          status="tertiary"
          iconRight="dots-three-vertical-bold"
        />
      </DNAContextMenu.Anchor>
      <DNAContextMenu.Items>
        <DNAContextMenu.Item
          collapseOnPress
          delay={100} // Workaround because sometimes the item deletes too fast before the ContextMenu can hide itself
          title="Delete"
          status="danger"
          iconStatus="danger"
          icon="trash"
          onPress={() => handleDeleteWidget(widgetType, rhForm, id)}
        />
      </DNAContextMenu.Items>
    </DNAContextMenu>
  )
}

type WidgetCardHeaderProps = {
  widget: ModifiedHubSection
  rightElement?: React.ReactElement
}

export const WidgetCardHeader: React.FC<WidgetCardHeaderProps> = (props) => {
  const { widget, rightElement } = props
  const { rhForm, isSaving } = useHubRHFromState()
  const { hubORM } = useHubsState()
  const { control, setValue, getValues, trigger, formState: { errors } } = rhForm
  const [editHeaderMode, setEditHeaderMode] = useState<boolean>(false)
  const [sectionHeader, setSectionHeader] = useState<string>(widget.sectionHeader || '')
  const [previousSectionHeader, setPreviousSectionHeader] = useState<string>(widget.sectionHeader || '')
  const headerRef = useRef<Input>(null)
  const formName = getWidgetFormName(widget.id)

  useEffect(() => {
    if (isSaving) {
      const widgetFormValue = getValues(formName)
      setEditHeaderMode(false)
      setSectionHeader(widgetFormValue?.sectionHeader || '')
      setPreviousSectionHeader(widgetFormValue?.sectionHeader || '')
    }
  }, [isSaving])

  useEffect(() => {
    if (editHeaderMode) headerRef.current?.focus()
  }, [editHeaderMode])

  const onDone = useCallback(() => {
    const widgetFormValue = getValues(formName)
    setSectionHeader(widgetFormValue?.sectionHeader || '')
    setPreviousSectionHeader(widgetFormValue?.sectionHeader || '')
    setEditHeaderMode(false)
  }, [getValues])

  const cancelHeaderChange = useCallback(() => {
    setEditHeaderMode(false)
    setSectionHeader(previousSectionHeader)
    setValue(`${formName}.sectionHeader`, previousSectionHeader)
    trigger(`${formName}.sectionHeader`)
  }, [getValues, setValue, previousSectionHeader])

  const lockIcon = useMemo(() => {
    if (hubORM?.model.hubType === HubType.TEMPLATE_INSTANCE) {
      return (
        <CommonIcon variant={widget.locked ? 'lock-key' : 'lock-simple-open'} />
      )
    } else if (hubORM?.model.hubType === HubType.TEMPLATE) {
      return (
        <Controller
          name={`${formName}.locked`}
          rules={{ required: false }}
          control={control}
          render={({ field: { value, onChange } }) => (
            // we dont really need the disablePopover here since we are checking it above. But it is here for consistency
            <DNAPopover disablePopover={hubORM?.model.hubType === HubType.STANDALONE}> 
              <DNAPopover.Anchor>
                <DNAButton
                  appearance="ghost"
                  status="subtle"
                  iconLeft={value ? 'lock-key' : 'lock-simple-open'}
                  onPress={() => onChange(!value)}
                  />
              </DNAPopover.Anchor>
              <DNAPopover.Content>
                <DNAText colorMode="dark">
                  This determines whether your audience can edit this section or not.
                </DNAText>
              </DNAPopover.Content>
            </DNAPopover>
          )}
        />
      )
    } else {
      return null
    }
  }, [hubORM?.model.hubType, control, formName, widget.locked])

  return (
    <DNABox
      fill
      style={widgetContentCardStyles.cardHeader}
    >
      <DNABox
        fill
        alignY="center"
        childFill={0}
      >
        <DNABox style={widgetContentCardStyles.lockIconContainer}>
          {lockIcon}
        </DNABox>

        {/* Hub Title Read Only Mode - Text & Edit Button & rightElement */}
        <Iffy is={!editHeaderMode}>
          <DNABox testID='widget-card-header' fill childFill={0} alignY="center">
            <DNAText
              h4
              bold
              numberOfLines={1}
              style={ widgetContentCardStyles.editHeaderInput }
              testID="widget-card-header-text"
            >
              {hubORM?.model.hubType === HubType.TEMPLATE_INSTANCE && widget.locked
                ? hubORM.model.hubSections?.find(section => section.id === widget.id)?.sectionHeader || ''
                : sectionHeader}
            </DNAText>
            <Iffy is={hubORM?.model.hubType !== HubType.TEMPLATE_INSTANCE || (hubORM?.model.hubType === HubType.TEMPLATE_INSTANCE && !widget.locked)}>
              <DNAButton
                appearance="ghost"
                status="subtle"
                iconLeft="pencil-simple"
                style={ widgetContentCardStyles.editHubTitleIcon }
                onPress={() => setEditHeaderMode(true)}
              />
            </Iffy>
          </DNABox>
          { rightElement }
        </Iffy>
        {/* Edit Hub Title Mode - Input & Buttons */}
        <Iffy is={editHeaderMode}>
        <DNABox fill alignY="center">
          <Controller
            name={`${formName}.sectionHeader`}
            rules={{ required: false }}
            control={control}
            defaultValue={sectionHeader}
            render={() => (
              <DNABox fill alignY="center" spacing="sm" childFill={0}>
                <TextField.Kitten
                  testID='hub-sub-header-text-input-box'
                  ref={headerRef}
                  status={errors.widgets?.[widget.id]?.sectionHeader && 'danger'}
                  onChangeText={(text) => {
                    setSectionHeader(text.trimStart())
                    rhForm.setValue(`${formName}.sectionHeader`, text.trimStart(), { shouldDirty: true })
                  }}
                  value={sectionHeader}
                  maxLength={150}
                  onKeyPress={(e) => {
                    // Text boxes may omit the section header when body content is present.
                    if (e.nativeEvent.key === 'Enter' && (widget.type === HubSectionType.TEXTAREA || sectionHeader)) {
                      e.preventDefault()
                      onDone()
                    }
                  }}
                  accessoryRight={() => (
                    sectionHeader.length > 0
                      ? <DNAButton
                          appearance="ghost"
                          status="subtle"
                          onPress={() => setSectionHeader('')}
                          iconLeft="x-circle-fill"
                      /> : <></>
                    )
                  }
                />
                <DNAButton
                  testID="hub-sub-title-cancel-btn"
                  appearance="outline"
                  status="tertiary"
                  onPress={cancelHeaderChange}
                  children="Cancel"
                />
                <DNAButton
                  testID="hub-sub-title-done-btn"
                  onPress={onDone}
                  disabled={ widget.type !== HubSectionType.TEXTAREA && !sectionHeader }
                  children="Done"
                />
              </DNABox>
            )}
          />
        </DNABox>
        </Iffy>
      </DNABox>
    </DNABox>
  )
}

export const WidgetCardBody: React.FC<PropsWithChildren> = (props) => {
  const { children, ...rest } = props
  return (
    <DNABox
      fill
      appearance="col"
      spacing="md"
      alignY="center"
      style={widgetContentCardStyles.cardBody}
      {...rest}
    >
      { children }
    </DNABox>
  )
}
